import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Spinner } from '../components/ui/Spinner.js';
import { Button } from '../components/ui/Button.js';
import { toast } from '../components/ui/Toast.js';
import { useAuthStore } from '../stores/auth-store.js';
import { useRoomStore } from '../stores/room-store.js';
import { RoomWebSocket } from '../lib/room-ws.js';
import { buildWsUrl, copyToClipboard } from '../lib/utils.js';
import { api, ApiError } from '../lib/api.js';
import { PreJoinScreen } from '../components/room/PreJoinScreen.js';
import type { WsServerMessage } from '@ember/types';

type Phase = 'prejoin' | 'waiting';

export default function LobbyPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);

  const setRoom = useRoomStore((s) => s.setRoom);
  const setAdmitted = useRoomStore((s) => s.setAdmitted);
  const setDenied = useRoomStore((s) => s.setDenied);
  const setLobbyQueue = useRoomStore((s) => s.setLobbyQueue);
  const resetRoom = useRoomStore((s) => s.resetRoom);
  const setWs = useRoomStore((s) => s.setWs);
  const setPreJoinPreferences = useRoomStore((s) => s.setPreJoinPreferences);

  const isAdmitted = useRoomStore((s) => s.isAdmitted);
  const isDenied = useRoomStore((s) => s.isDenied);
  const realtimekitToken = useRoomStore((s) => s.realtimekitToken);

  const [phase, setPhase] = useState<Phase>('prejoin');

  // Redirect to room once admitted (token set)
  useEffect(() => {
    if (isAdmitted && realtimekitToken && slug) {
      navigate(`/r/${slug}/room`, { replace: true });
    }
  }, [isAdmitted, realtimekitToken, slug, navigate]);

  // Only connect WebSocket after the user clicks "Join now" on the pre-join screen
  useEffect(() => {
    if (phase !== 'waiting') return;
    if (!slug || !user) return;

    // Don't re-connect if WS already live (e.g. back-navigation)
    const existingWs = useRoomStore.getState().ws;
    if (existingWs) return;

    // 1. Fetch room metadata
    api
      .get<{ room: import('@ember/types').Room; isHost: boolean }>(`/api/rooms/${slug}`)
      .then(({ room, isHost }) => {
        setRoom(room, slug);

        // 2. Create WebSocket and store it globally so RoomPage can reuse it
        const ws = new RoomWebSocket();
        setWs(ws);

        ws.onMessage((msg: WsServerMessage) => {
          switch (msg.type) {
            case 'admitted':
              setAdmitted(msg.realtimekitToken);
              break;
            case 'denied':
              setDenied();
              break;
            case 'lobby_update':
              setLobbyQueue(msg.waitingParticipants);
              break;
            case 'room_expired':
              toast.error('This room has expired.');
              resetRoom();
              navigate('/', { replace: true });
              break;
            case 'error':
              toast.error(msg.message);
              break;
          }
        });

        ws.onClose(() => {
          const state = useRoomStore.getState();
          if (!state.isAdmitted && !state.isDenied) {
            toast.error('Connection lost. The room may have ended.');
          }
        });

        const wsUrl = buildWsUrl(slug, {
          userId: user!.id,
          displayName: user!.displayName,
          isHost,
        });
        ws.connect(wsUrl);
      })
      .catch((err) => {
        const msg = err instanceof ApiError ? err.message : 'Could not load room.';
        toast.error(msg);
        navigate('/', { replace: true });
      });

    // Do NOT return a cleanup that closes the WS — it must survive navigation to RoomPage.
  }, [phase, slug, user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  async function handleCopyLink() {
    const url = `${window.location.origin}/r/${slug}`;
    const ok = await copyToClipboard(url);
    if (ok) toast.success('Link copied!');
    else toast.error('Failed to copy link.');
  }

  // ── Pre-join screen ───────────────────────────────────────────────────────
  if (phase === 'prejoin') {
    return (
      <PreJoinScreen
        slug={slug ?? ''}
        displayName={user?.displayName ?? ''}
        onJoin={(prefs) => {
          setPreJoinPreferences(prefs);
          setPhase('waiting');
        }}
        onCancel={() => { resetRoom(); navigate('/'); }}
      />
    );
  }

  // ── Denied ────────────────────────────────────────────────────────────────
  if (isDenied) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-neutral-950 px-4 text-center">
        <span className="text-4xl">🚫</span>
        <h1 className="mt-4 text-xl font-bold text-neutral-100">Entry denied</h1>
        <p className="mt-2 text-neutral-400">The host didn't let you in.</p>
        <Button className="mt-6" onClick={() => { resetRoom(); navigate('/'); }}>
          Back to home
        </Button>
      </div>
    );
  }

  // ── Waiting ───────────────────────────────────────────────────────────────
  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-neutral-950 px-4">
      <div className="w-full max-w-sm text-center">
        <span className="text-4xl">⏳</span>
        <h1 className="mt-4 text-xl font-bold text-neutral-100">Waiting to be admitted</h1>
        <p className="mt-2 text-neutral-400 text-sm">
          The host will let you in shortly.
        </p>

        <div className="mt-6 flex flex-col items-center gap-3">
          <Spinner size="md" />
          <p className="text-xs text-neutral-600">
            Room: <span className="font-mono text-neutral-400">{slug}</span>
          </p>
        </div>

        <div className="mt-8 flex flex-col gap-3">
          <Button variant="secondary" onClick={handleCopyLink} className="w-full">
            📋 Copy invite link
          </Button>
          <Button
            variant="ghost"
            onClick={() => { resetRoom(); navigate('/'); }}
            className="w-full"
          >
            Leave
          </Button>
        </div>

        <p className="mt-6 text-xs text-neutral-600">
          🔒 End-to-end encrypted · emberchat.link
        </p>
      </div>
    </div>
  );
}
